import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeftRight, Calculator, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (v) => v != null ? new Intl.NumberFormat("pt-PT", { style: "currency", currency: "EUR" }).format(v) : "—";
const pct = (v) => v != null ? `${v.toFixed(1)}%` : "—";

const ScenarioForm = ({ label, color, data, setData }) => {
  const isFreelancer = data.regime_type === "green_receipts" || data.regime_type === "b2b";
  return (
    <div className={`p-4 rounded-lg border-2 ${color}`}>
      <p className="text-sm font-bold mb-3">{label}</p>
      <div className="space-y-3">
        <div>
          <Label className="text-xs">Regime</Label>
          <Select value={data.regime_type} onValueChange={v => setData({...data, regime_type: v})}>
            <SelectTrigger className="mt-1 h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="contract">Contrato</SelectItem>
              <SelectItem value="green_receipts">Recibos Verdes</SelectItem>
              <SelectItem value="b2b">B2B</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {isFreelancer ? (
          <div>
            <Label className="text-xs">Valor / Dia (€)</Label>
            <Input type="number" value={data.daily_rate} onChange={e => setData({...data, daily_rate: e.target.value})} className="mt-1 h-8 text-xs" />
          </div>
        ) : (
          <div>
            <Label className="text-xs">Salário Bruto (€)</Label>
            <Input type="number" value={data.gross_value} onChange={e => setData({...data, gross_value: e.target.value})} className="mt-1 h-8 text-xs" />
          </div>
        )}
        <div>
          <Label className="text-xs">Rate Cliente (€/h)</Label>
          <Input type="number" value={data.client_rate} onChange={e => setData({...data, client_rate: e.target.value})} className="mt-1 h-8 text-xs" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs">Meses Fat.</Label>
            <Input type="number" value={data.billable_months} onChange={e => setData({...data, billable_months: e.target.value})} className="mt-1 h-8 text-xs" />
          </div>
          <div>
            <Label className="text-xs">Refeição/dia</Label>
            <Input type="number" value={data.meal_allowance_daily} onChange={e => setData({...data, meal_allowance_daily: e.target.value})} className="mt-1 h-8 text-xs" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 pt-2 border-t border-slate-200">
          <div>
            <Label className="text-xs">Deslocação (€/mês)</Label>
            <Input type="number" value={data.km_allowance} onChange={e => setData({...data, km_allowance: e.target.value})} className="mt-1 h-8 text-xs" data-testid="cmp-km-allowance" />
          </div>
          <div>
            <Label className="text-xs">Seguro Real Vida (€/mês)</Label>
            <Input type="number" value={data.insurance_capitalization} onChange={e => setData({...data, insurance_capitalization: e.target.value})} className="mt-1 h-8 text-xs" data-testid="cmp-real-vida" />
          </div>
          <div>
            <Label className="text-xs">Ticket Infância (€/mês)</Label>
            <Input type="number" value={data.ticket_infancia} onChange={e => setData({...data, ticket_infancia: e.target.value})} className="mt-1 h-8 text-xs" data-testid="cmp-ticket-infancia" />
          </div>
          <div>
            <Label className="text-xs">Seguro Saúde (€/mês)</Label>
            <Input type="number" value={data.health_insurance_cost} onChange={e => setData({...data, health_insurance_cost: e.target.value})} className="mt-1 h-8 text-xs" data-testid="cmp-health-insurance" />
          </div>
        </div>
      </div>
    </div>
  );
};

const ResultColumn = ({ label, color, result }) => {
  if (!result) return (
    <div className={`p-4 rounded-lg border-2 ${color} flex items-center justify-center min-h-[200px]`}>
      <p className="text-xs text-muted-foreground">Clique em Comparar para ver resultados</p>
    </div>
  );

  const r = result.results?.[0] || {};
  const scoreColor = r.score_color === "green" ? "bg-brand-500" : r.score_color === "lime" ? "bg-lime-400" : r.score_color === "yellow" ? "bg-yellow-500" : r.score_color === "orange" ? "bg-orange-500" : "bg-red-500";

  const billable = parseFloat(result.input?.billable_months ?? 11);
  const monthlyRevenue = r.client_revenue || 0;
  const monthlyCost = r.total_company_cost || 0;
  const monthlyProfit = monthlyRevenue - monthlyCost; // lucro da EMPRESA por mês
  const annualRevenue = monthlyRevenue * billable;
  const annualCost = monthlyCost * 12; // custos fixos suportados todos os meses
  const annualMargin = annualRevenue - annualCost;

  return (
    <div className={`p-4 rounded-lg border-2 ${color}`}>
      <p className="text-sm font-bold mb-3">{label}</p>
      <div className={`${scoreColor} text-white text-center py-2 rounded-lg mb-3`}>
        <span className="text-lg font-bold">{pct(r.margin_percentage)}</span>
        <span className="text-xs ml-2 opacity-80">{r.score_label}</span>
      </div>
      <div className="space-y-2 text-xs">
        <Row label="Bruto Mensal" value={fmt(r.gross_monthly)} />
        <Row label="Líquido Mensal Colaborador" value={fmt(r.net_monthly)} muted />
        <Row label="Lucro Mensal (Empresa)" value={fmt(monthlyProfit)} bold green />
        <Row label="SS Empresa" value={fmt(r.social_security_employer)} muted />
        <Row label="SS Trabalhador" value={fmt(r.social_security_employee || r.ss_employee)} muted />
        <Row label="IRS" value={fmt(r.irs_retention)} muted />
        <Row label={`Faturação Anual (×${billable}m)`} value={fmt(annualRevenue)} />
        <Row label="Custo Anual (×12m)" value={fmt(annualCost)} muted />
        <Row label="Margem Anual" value={fmt(annualMargin)} bold green />
      </div>
    </div>
  );
};

const Row = ({ label, value, bold, muted, green }) => (
  <div className="flex justify-between items-center">
    <span className={muted ? "text-muted-foreground" : "text-slate-600"}>{label}</span>
    <span className={`${bold ? "font-bold" : ""} ${green ? "text-brand-600" : "text-slate-800"}`}>{value}</span>
  </div>
);

const ComparisonView = ({ getAuthHeaders }) => {
  const [scenarioA, setScenarioA] = useState({
    regime_type: "contract", gross_value: "2000", daily_rate: "", client_rate: "30",
    client_rate_type: "hourly", billable_months: "11", meal_allowance_daily: "9.60",
    business_type: "outsourcing", value_type: "monthly",
    km_allowance: "0", insurance_capitalization: "0", ticket_infancia: "0", health_insurance_cost: "0",
  });
  const [scenarioB, setScenarioB] = useState({
    regime_type: "green_receipts", gross_value: "", daily_rate: "150", client_rate: "30",
    client_rate_type: "hourly", billable_months: "11", meal_allowance_daily: "0",
    business_type: "outsourcing", value_type: "monthly",
    km_allowance: "0", insurance_capitalization: "0", ticket_infancia: "0", health_insurance_cost: "0",
  });
  const [resultA, setResultA] = useState(null);
  const [resultB, setResultB] = useState(null);
  const [loading, setLoading] = useState(false);

  const compare = async () => {
    setLoading(true);
    try {
      const buildPayload = (s) => {
        const km = parseFloat(s.km_allowance) || 0;
        const ins = parseFloat(s.insurance_capitalization) || 0;
        const ticket = parseFloat(s.ticket_infancia) || 0;
        const health = parseFloat(s.health_insurance_cost) || 0;
        return {
          ...s,
          gross_value: parseFloat(s.gross_value) || 0,
          daily_rate: parseFloat(s.daily_rate) || 0,
          client_rate: parseFloat(s.client_rate) || 0,
          billable_months: parseFloat(s.billable_months) || 11,
          meal_allowance_daily: parseFloat(s.meal_allowance_daily) || 0,
          include_meal_allowance: parseFloat(s.meal_allowance_daily) > 0,
          meal_allowance_card: true,
          km_allowance: km,
          insurance_capitalization: ins,
          include_insurance: ins > 0,
          ticket_infancia: ticket,
          health_insurance_cost: health,
          include_health_insurance: health > 0,
        };
      };
      const [resA, resB] = await Promise.all([
        axios.post(`${API}/simulator/calculate`, buildPayload(scenarioA), { headers: getAuthHeaders() }),
        axios.post(`${API}/simulator/calculate`, buildPayload(scenarioB), { headers: getAuthHeaders() }),
      ]);
      setResultA(resA.data);
      setResultB(resB.data);
      toast.success("Comparação concluída!");
    } catch (err) {
      toast.error("Erro ao comparar cenários");
    } finally {
      setLoading(false);
    }
  };

  const rA = resultA?.results?.[0];
  const rB = resultB?.results?.[0];
  const hasBoth = rA && rB;

  return (
    <div className="space-y-6" data-testid="comparison-view">
      {/* Inputs side by side */}
      <div className="grid grid-cols-2 gap-4">
        <ScenarioForm label="Cenário A" color="border-blue-300 bg-blue-50/30" data={scenarioA} setData={setScenarioA} />
        <ScenarioForm label="Cenário B" color="border-violet-300 bg-violet-50/30" data={scenarioB} setData={setScenarioB} />
      </div>

      <div className="flex justify-center">
        <Button onClick={compare} disabled={loading} className="px-8 bg-[var(--brand-primary)] hover:bg-[var(--brand-primary)] text-white" data-testid="compare-btn">
          {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowLeftRight className="w-4 h-4 mr-2" />}
          Comparar
        </Button>
      </div>

      {/* Results side by side */}
      <div className="grid grid-cols-2 gap-4">
        <ResultColumn label="Cenário A" color="border-blue-300 bg-blue-50/30" result={resultA} />
        <ResultColumn label="Cenário B" color="border-violet-300 bg-violet-50/30" result={resultB} />
      </div>

      {/* Difference summary */}
      {hasBoth && (() => {
        const profitA = (rA.client_revenue || 0) - (rA.total_company_cost || 0);
        const profitB = (rB.client_revenue || 0) - (rB.total_company_cost || 0);
        // client_revenue já é média 12m (rate × meses faturáveis / 12) — anual vem do backend
        const annualOf = (r) => (r.annual_revenue ?? (r.client_revenue || 0) * 12) - (r.total_company_cost_annual ?? (r.total_company_cost || 0) * 12);
        const annualMA = annualOf(rA);
        const annualMB = annualOf(rB);
        return (
          <div className="p-4 rounded-lg border-2 border-slate-200 bg-slate-50" data-testid="comparison-summary">
            <p className="text-sm font-bold mb-3 flex items-center gap-2"><ArrowLeftRight className="w-4 h-4" /> Diferença (A vs B)</p>
            <div className="grid grid-cols-3 gap-4 text-xs">
              <DiffItem label="Lucro Mensal (Empresa)" valueA={profitA} valueB={profitB} />
              <DiffItem label="Margem %" valueA={rA.margin_percentage} valueB={rB.margin_percentage} isPct />
              <DiffItem label="Margem Anual" valueA={annualMA} valueB={annualMB} />
            </div>
          </div>
        );
      })()}
    </div>
  );
};

const DiffItem = ({ label, valueA, valueB, isPct }) => {
  const diff = (valueA || 0) - (valueB || 0);
  const positive = diff >= 0;
  return (
    <div className="text-center p-3 rounded-lg bg-white border">
      <p className="text-muted-foreground mb-1">{label}</p>
      <p className={`text-lg font-bold ${positive ? "text-brand-600" : "text-red-600"}`}>
        {positive ? "+" : ""}{isPct ? pct(diff) : fmt(diff)}
      </p>
      <p className="text-[10px] text-muted-foreground mt-0.5">A: {isPct ? pct(valueA) : fmt(valueA)} | B: {isPct ? pct(valueB) : fmt(valueB)}</p>
    </div>
  );
};

export default ComparisonView;
